/**
 * Permission codes the Quality Management screens declare an interest in.
 *
 * These are the dot-strings `internal/qms/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`.
 *
 * **Two segregation-of-duties pairs run through this module and no screen ever
 * infers one half from the other:**
 *
 *  - `inspectionRecord` / `dispositionDecide` — the inspector who finds the
 *    defect must not be the one who decides the goods are acceptable anyway.
 *    This is inspector independence expressed as an authority pair, and it sits
 *    *beside* the per-plan `requiresIndependentInspector` check, which is about
 *    the origin document's actor rather than about who holds what.
 *  - `capaManage` / `capaVerify` — whoever ran the corrective action must not be
 *    the one who certifies that it worked.
 *
 * So the recording sheet and the disposition dialog are separately permissioned
 * surfaces and a user holding one never sees a disabled version of the other.
 *
 * `moduleManage` guards no route. It is the `<module>.manage` fallback
 * `core/rules` consults when no authority band covers an overridable rule's doc
 * type; without it a concession override returns `ERR_OVERRIDE_NOT_AUTHORISED`
 * on a tenant with no matrix. It is listed so the screens can explain a refusal
 * rather than merely report one.
 *
 * Four codes belong to other modules and are here because these screens cannot
 * do their job without them: the company an inspection plan is written for, the
 * item it inspects, the warehouse a quarantined lot sits in, and the vendor a
 * receipt plan narrows to.
 */

export const QMS_PERMISSIONS = {
  planRead: 'qms.plan.read',
  planManage: 'qms.plan.manage',

  inspectionRead: 'qms.inspection.read',
  /** The inspector's own permission. SoD partner of `dispositionDecide`. */
  inspectionRecord: 'qms.inspection.record',
  /** Releasing without inspecting, with a reason. */
  inspectionWaive: 'qms.inspection.waive',

  /** SoD partner of `inspectionRecord`. */
  dispositionDecide: 'qms.disposition.decide',

  capaRead: 'qms.capa.read',
  /** SoD partner of `capaVerify`. */
  capaManage: 'qms.capa.manage',
  /** SoD partner of `capaManage`. The effectiveness verdict, not the actions. */
  capaVerify: 'qms.capa.verify',

  settingsManage: 'qms.settings.manage',

  /** Guards no route: the `<module>.manage` fallback for rule overrides. */
  moduleManage: 'qms.manage',

  /* ------------------------------------------------- other modules' codes */

  companyRead: 'org.company.read',
  itemRead: 'inventory.item.read',
  warehouseRead: 'inventory.warehouse.read',
  vendorRead: 'ap.vendor.read',
} as const

export type QmsPermission = (typeof QMS_PERMISSIONS)[keyof typeof QMS_PERMISSIONS]
